import Button from 'tio-reimagined/components/button';
import { concat } from '@ember/helper';
import { getTranslationKeyForReportedSalaryForMatchPlan } from 'tio-common/utils/syf';
import { gt } from 'tio-ui/utilities';
import { LinkTo } from '@ember/routing';
import { on } from '@ember/modifier';
import { PhPencil, PhInfo, PhWarning } from 'ember-phosphor-icons';
import { Section } from 'tio-reimagined/components/layout';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import formatCentsToDollars from 'tio-common/helpers/format-cents-to-dollars';
import safeFormatNumber from 'tio-common/helpers/safe-format-number';
import SyfDashboardInsightsParticipantFormDialog from './insights/participant-form-dialog';
import type { IntlService } from 'ember-intl';
import type AccountModel from 'tio-data/models/account';
import type MatchParticipantModel from 'tio-data/models/match-participant';
import type MatchPlanModel from 'tio-data/models/match-plan';
import type PartnerService from 'tio-employee/services/partner';

interface SyfDashboardInsightsSignature {
  Args: {
    matchParticipant: MatchParticipantModel;
    matchPlan: MatchPlanModel;
    accounts: AccountModel[];
  };
  Element: HTMLDivElement;
}

class SyfDashboardInsightsComponent extends Component<SyfDashboardInsightsSignature> {
  @service declare partner: PartnerService;
  @service declare intl: IntlService;

  @tracked isEditingParticipantInfo = false;

  toggleEditParticipantInfo = () => {
    this.isEditingParticipantInfo = !this.isEditingParticipantInfo;
  };

  didSaveParticipantInfo = () => {
    this.toggleEditParticipantInfo();
    this.args.matchParticipant.fetchInsights();
  };

  get otherRepaymentOptionsLink() {
    return this.partner.isPslfEnabled
      ? 'authenticated.syf.insights.pslf'
      : 'authenticated.syf.insights.idr';
  }

  get qualifyingIncomeFieldLabel() {
    return this.intl.t(getTranslationKeyForReportedSalaryForMatchPlan(this.args.matchPlan));
  }

  get isMissingInfoForDeferralInsight() {
    const { matchParticipant } = this.args;

    return !matchParticipant.hasDataForInsights;
  }

  get showDeferralInsightCard() {
    return !this.isMissingInfoForDeferralInsight;
  }

  <template>
    <Section>
      <:header>{{t "syf.dashboard.insights.header"}}</:header>
      <:body>
        <div class="tio-col-loose">
          {{t "syf.dashboard.insights.section.one.discover"}}
          <dl>
            <dt class="font-semibold mb-1">{{t
                "syf.dashboard.insights.estimated_monthly_payment"
              }}</dt>
            <dd class="tio-row-between mb-2">
              {{#if @matchParticipant.reportedMonthlyPaymentInDollars}}
                {{safeFormatNumber
                  @matchParticipant.reportedMonthlyPaymentInDollars
                  style="currency"
                  currency="USD"
                }}
              {{else}}
                {{t "common.not_provided"}}
              {{/if}}
              <Button
                @appearance="outlined"
                title={{concat
                  (t "common.edit")
                  " "
                  (t "syf.dashboard.insights.section.one.estimated")
                }}
                {{on "click" this.toggleEditParticipantInfo}}
              >
                <PhPencil @size="20" />
              </Button>
            </dd>

            <dt class="font-semibold mb-1">{{this.qualifyingIncomeFieldLabel}}</dt>
            <dd class="tio-row-between mb-2">
              {{#if @matchParticipant.reportedSalaryInDollars}}
                {{safeFormatNumber
                  @matchParticipant.reportedSalaryInDollars
                  style="currency"
                  currency="USD"
                }}
              {{else}}
                {{t "common.not_provided"}}
              {{/if}}
              <Button
                @appearance="outlined"
                title={{concat (t "common.edit") " " this.qualifyingIncomeFieldLabel}}
                {{on "click" this.toggleEditParticipantInfo}}
              >
                <PhPencil @size="20" />
              </Button>
            </dd>
          </dl>

          {{#if this.isMissingInfoForDeferralInsight}}
            <div class="tio-row-top-loose tio-fg-danger">
              <PhWarning @size="48" />
              {{t "syf.dashboard.insights.section.one.missing_both"}}
            </div>
          {{else}}
            <div class="tio-row-top-loose">
              <PhInfo @size="48" />
              {{t "syf.dashboard.insights.section.one.changes_will_impact"}}
            </div>
          {{/if}}

          {{#if this.showDeferralInsightCard}}
            <Section>
              <:header>
                {{t "syf.dashboard.insights.section.one.header"}}
              </:header>
              <:body>
                <p>
                  {{#if (gt @matchParticipant.insights.defermentDeltaPerMonth 0)}}
                    {{t
                      "syf.dashboard.insights.section.one.content_below_max"
                      deferment_amount=(formatCentsToDollars
                        @matchParticipant.insights.defermentDeltaPerMonth defaultValue="--"
                      )
                    }}
                  {{else}}
                    {{t "syf.dashboard.insights.section.one.content_at_max"}}
                  {{/if}}
                </p>

                <LinkTo
                  @route="authenticated.syf.insights.four-oh-one-k"
                  class="tio-anchor block mt-2"
                >
                  {{t "syf.dashboard.insights.section.one.read_more"}}
                </LinkTo>
              </:body>
            </Section>
          {{/if}}

          <Section>
            <:header>
              <div class="flex items-start">
                {{t "syf.dashboard.insights.section.two.header"}}
              </div>
            </:header>
            <:body>
              <p>{{t "syf.dashboard.insights.section.two.content"}}</p>

              <LinkTo @route={{this.otherRepaymentOptionsLink}} class="tio-anchor block mt-2">
                {{t "syf.dashboard.insights.section.two.read_more"}}
              </LinkTo>
            </:body>
          </Section>
        </div>
      </:body>
    </Section>

    {{#if this.isEditingParticipantInfo}}
      <SyfDashboardInsightsParticipantFormDialog
        @matchParticipant={{@matchParticipant}}
        @matchPlan={{@matchPlan}}
        @onCancel={{this.toggleEditParticipantInfo}}
        @didSubmit={{this.didSaveParticipantInfo}}
      />
    {{/if}}
  </template>
}

export default SyfDashboardInsightsComponent;
